import { KatDropdown } from '@amzn/katal-react';
import { useState, useCallback } from 'react';
import { StyledDropdown } from 'src/components/general/StyledDropdown';
import { AutoDismissConfirmationDialog } from 'src/components/general/AutoDismissConfirmationDialog';
import { useSelection } from 'src/contexts/SelectionContext';
import { DeviceFamily } from '@amzn/fire-tv-campaign-automation-service';
import { getDeviceFamilyOptionsByStage } from 'src/config/deviceFamilyConfiguration';
import { ProvisionedConstants, STAGE } from 'src/constants';
import { useAssetOperationsInProgress } from 'src/hooks/useAssetOperationsInProgress';

const DeviceFamilySelector = () => {
  const { deviceFamily, setDeviceFamily } = useSelection();
  const isTransforming = useAssetOperationsInProgress();
  const deviceFamilyOptions = getDeviceFamilyOptionsByStage(ProvisionedConstants.STAGE as STAGE);
  const [pendingDeviceFamily, setPendingDeviceFamily] = useState<DeviceFamily | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [dropdownKey, setDropdownKey] = useState(0);

  const handleDeviceFamilyChange = (event: KatDropdown.ChangeEvent) => {
    const newDeviceFamily = event.detail.value as DeviceFamily;

    if (newDeviceFamily !== deviceFamily) {
      setPendingDeviceFamily(newDeviceFamily);
      setShowConfirmation(true);
    }
  };

  const handleConfirmDeviceFamilyChange = useCallback(() => {
    if (pendingDeviceFamily) {
      setDeviceFamily(pendingDeviceFamily);
      window.location.reload();
    }
  }, [pendingDeviceFamily, setDeviceFamily]);

  const handleCancelDeviceFamilyChange = () => {
    setShowConfirmation(false);
    setPendingDeviceFamily(null);
    setDropdownKey((prev) => prev + 1);
  };

  return (
    <>
      <StyledDropdown
        key={dropdownKey}
        value={deviceFamily}
        onChange={handleDeviceFamilyChange}
        options={deviceFamilyOptions}
        disabled={isTransforming}
        data-testid="device-family-selector"
      />

      <AutoDismissConfirmationDialog
        isVisible={showConfirmation}
        title="Confirm Device Family Change"
        message="Changing the device family will reset all form data and reload the page. Do you want to continue?"
        confirmText="Continue"
        cancelText="Cancel"
        confirmVariant="primary"
        autoConfirmTimeout={10000}
        onConfirm={handleConfirmDeviceFamilyChange}
        onCancel={handleCancelDeviceFamilyChange}
        data-testid="device-family-confirmation-dialog"
      />
    </>
  );
};

export { DeviceFamilySelector };
